import { t } from "@/i18n-js/instance";
import {
  ACTION_TYPE,
  type BadgeProperty,
  type BenefitProperty,
  COUNTDOWN_TYPE,
  type PaywallCustomization,
  type TestimonialProperty,
} from "@/react/types/PaywallCustomization";
import { useSpacesContext } from "@circle-react/contexts";
import { usePunditUserContext } from "@circle-react/contexts";
import { REMAINING_TIME_DEFAULT_VALUE } from "@circle-react/helpers/paywallCustomizationHelpers";
import { normalizeAccessGroupSources } from "@circle-react-shared/AccessGroups/helpers";
import {
  DISPLAY_STYLE_DEFAULT_COLORS,
  DISPLAY_STYLE_OPTIONS,
} from "../CheckoutTab/constants";

export const emptyTestimonial: TestimonialProperty = {
  name: t(
    "settings.paywalls.modal.tabs.checkout.builder.testimonials.fields.name_placeholder",
  ),
  avatar_image_url: "",
  avatar_image: "",
  quote: t(
    "settings.paywalls.modal.tabs.checkout.builder.testimonials.fields.quote_placeholder",
  ),
  headline: t(
    "settings.paywalls.modal.tabs.checkout.builder.testimonials.fields.headline_placeholder",
  ),
  visible: true,
  display_style: {
    type: DISPLAY_STYLE_OPTIONS.STARS,
    color: DISPLAY_STYLE_DEFAULT_COLORS.STARS,
  },
};

export const emptyBadge: BadgeProperty = {
  background_color: "#F6F6F6",
  icon: { emoji_type: "emoji", emoji: "😀" },
  label: t(
    "settings.paywalls.modal.tabs.checkout.builder.badges.fields.label_placeholder",
  ),
  text_color: "#000000",
  visible: true,
};

export const emptyBenefit: BenefitProperty = {
  title: t(
    "settings.paywalls.modal.tabs.checkout.builder.benefits.fields.title_placeholder",
  ),
  description: t(
    "settings.paywalls.modal.tabs.checkout.builder.benefits.fields.description_placeholder",
  ),
  icon: {
    emoji_type: "emoji",
    emoji: "✔️",
  },
};

export const getDefaultArray = <T>(
  input: T[] | null | undefined,
  defaultValue: T,
): T[] => {
  if (input === null || input === undefined || input.length === 0) {
    return [{ ...defaultValue }];
  }

  return input;
};

export const getPaywallCustomizationDefault = (
  paywallCustomization?: PaywallCustomization,
): Partial<PaywallCustomization> | Record<string, never> => {
  const benefitProperties = paywallCustomization?.benefit_properties;
  const testimonialProperties = paywallCustomization?.testimonial_properties;
  const badgeProperties = paywallCustomization?.badge_properties;
  const countdownProperties = paywallCustomization?.countdown_properties;

  const displayedTextDefault = t(
    "settings.paywalls.modal.tabs.checkout.builder.countdown.fields.displayed_text_default",
  );
  const displayedTextSmallerDefault = t(
    "settings.paywalls.modal.tabs.checkout.builder.countdown.fields.displayed_text_default_smaller",
  );
  const displayedText =
    displayedTextDefault.length >= 30
      ? displayedTextSmallerDefault
      : displayedTextDefault;

  return {
    ...(paywallCustomization || {}),
    countdown_properties: {
      enabled: countdownProperties?.enabled ?? false,
      countdown_type:
        countdownProperties?.countdown_type || COUNTDOWN_TYPE.REMAINING_TIME,
      remaining_time:
        countdownProperties?.remaining_time || REMAINING_TIME_DEFAULT_VALUE,
      remaining_time_interval: "minutes",
      finish_at: countdownProperties?.finish_at
        ? new Date(countdownProperties?.finish_at).toString()
        : new Date().toString(),
      displayed_text: countdownProperties?.displayed_text || displayedText,
      action: {
        action_type:
          countdownProperties?.action?.action_type || ACTION_TYPE.DO_NOTHING,
        redirect_to: countdownProperties?.action?.redirect_to || "",
      },
    },
    benefit_properties: {
      enabled: benefitProperties?.enabled ?? false,
      title:
        benefitProperties?.title ??
        t(
          "settings.paywalls.modal.tabs.checkout.builder.benefits.title_placeholder",
        ),
      benefits: getDefaultArray<BenefitProperty>(
        benefitProperties?.benefits,
        emptyBenefit,
      ),
    },
    testimonial_properties: {
      enabled: testimonialProperties?.enabled ?? false,
      testimonials: getDefaultArray<TestimonialProperty>(
        testimonialProperties?.testimonials,
        emptyTestimonial,
      ),
    },
    badge_properties: {
      enabled: badgeProperties?.enabled ?? false,
      badges: getDefaultArray<BadgeProperty>(
        badgeProperties?.badges,
        emptyBadge,
      ),
    },
  };
};

export const usePaywallFormDefaultValues = () => {
  const { records: spaceRecords } = useSpacesContext();

  const getDefaultSpaceIds = (
    spaceIdsSelected: any,
    spaceGroupIdsSelected: any,
  ) => {
    const newSpaceIdsSelect = [...spaceIdsSelected];
    spaceRecords.forEach((space: any) => {
      if (spaceGroupIdsSelected.includes(space.space_group_id)) {
        newSpaceIdsSelect.push(space.id);
      }
    });

    return Array.from(new Set(newSpaceIdsSelect));
  };

  const { currentCommunitySettings } = usePunditUserContext();
  const isAccessGroupsEnabled = currentCommunitySettings?.access_groups_enabled;

  const getDefaultValues = (paywallParam: any) => {
    const defaultPaywall = {
      trial_days: 0,
      trial_requires_card: false,
      can_be_repurchased: false,
      stripe_prices_attributes: [],
      mobile_price_packages_attributes: [],
      all_spaces: false,
      space_group_ids: [],
      space_ids: [],
      redirect_url_enabled: false,
      thank_you_page_enabled: true,
      post_purchase_email_enabled: true,
      self_trial_cancelable: false,
      mobile_paywall_resource_attributes: {
        id: null,
        processor_id: null,
      },
      post_purchase_email_message: t(
        "settings.paywalls.form.post_purchase_email_default_message_text",
      ),
      post_purchase_email_meta: {
        subject: t(
          "settings.paywalls.form.post_purchase_email_default_subject_text",
        ),
      },
      currency_id: "",
      conversion_tracking_js_code: "",
      access_groups_enabled: isAccessGroupsEnabled,
      access_groups: [],
      paywall_customization_attributes: {
        ...getPaywallCustomizationDefault(),
      },
    };

    if (!paywallParam) {
      return defaultPaywall;
    }

    return {
      ...defaultPaywall,
      ...paywallParam,
      redirect_url_enabled: !!paywallParam?.redirect_url,
      enable_trial: paywallParam?.trial_days > 0,
      enable_subscription_group: !!paywallParam?.paywall_group_id,
      space_ids: getDefaultSpaceIds(
        paywallParam.space_ids,
        paywallParam.space_group_ids,
      ),
      space_group_ids: paywallParam.space_group_ids ?? [],
      currency_id: paywallParam?.currency?.id ?? "",
      access_groups_enabled: paywallParam?.access_groups_enabled,
      access_groups: normalizeAccessGroupSources(
        paywallParam?.access_group_sources,
      ),
      paywall_customization_attributes: {
        ...getPaywallCustomizationDefault(paywallParam?.paywall_customization),
      },
    };
  };

  return {
    getDefaultValues,
  };
};
